import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowRight,
  CheckCircle,
  Sparkles,
  Wrench,
  Shield,
  Settings,
  Users
} from 'lucide-react';
import { mockServices } from '../mock';

const iconMap = {
  Sparkles: Sparkles,
  Wrench: Wrench,
  Shield: Shield,
  Settings: Settings,
  Users: Users
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Our Services</Badge>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Quality Services Delivered
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We provide end-to-end integrated facility solutions designed to improve operational efficiency, maintain regulatory compliance, and reduce management burden.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="space-y-12">
            {mockServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <CardContent className="p-0">
                    <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                      <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover min-h-[300px]"
                        />
                      </div>
                      <div className="p-8 md:p-12 flex flex-col justify-center">
                        <div className="bg-[#1C4BB6]/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                          <Icon className="text-[#1C4BB6]" size={32} />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h2>
                        <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                        <ul className="space-y-3 mb-8">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={18} />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Link to="/contact">
                          <Button className="bg-[#1C4BB6] hover:bg-[#154096] text-white transition-all duration-300 hover:shadow-lg">
                            Request Service <ArrowRight className="ml-2" size={18} />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">Industries</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Industries We Serve</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our comprehensive facility management solutions cater to diverse sectors across India.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: "Industrial Units & Factories", description: "Complete facility management for manufacturing units" },
              { title: "Corporate Offices & IT Parks", description: "Professional workspace management solutions" },
              { title: "Educational Institutions", description: "Campus facility and maintenance services" },
              { title: "Healthcare Centers & Clinics", description: "Specialized hygiene and safety services" },
              { title: "Residential Complexes", description: "Housing society management solutions" },
              { title: "Commercial Complexes", description: "Mall and retail facility management" }
            ].map((industry, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{industry.title}</h3>
                  <p className="text-gray-600">{industry.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <Badge className="bg-[#1C4BB6]/10 text-[#1C4BB6] mb-4">FAQ</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-gray-600">
              Common questions about our facility management services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-[#1C4BB6]">
                What services does Ozone A-1 Services provide?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We provide comprehensive integrated facility management services including housekeeping, engineering services, security & safety, maintenance & repair, and staffing solutions for commercial, industrial, and residential facilities.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-[#1C4BB6]">
                Are your staff trained and verified?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Yes, all our personnel undergo professional training, safety orientation, skill-based assessment, and strict background verification before deployment. We maintain a structured onboarding process.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-[#1C4BB6]">
                Do you ensure compliance with labour laws?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Absolutely. We maintain full compliance with PF, ESIC, and all labour laws. We follow transparent payroll management and statutory compliance procedures for all our workforce.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-[#1C4BB6]">
                What areas do you serve?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We primarily serve Indore and surrounding areas in Madhya Pradesh. For projects outside this region, please contact us to discuss your specific requirements.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-[#1C4BB6]">
                How do I request a quote?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                You can request a quote by filling out our contact form, calling us directly at +91 8982682619, or emailing us at md@ozoneservices.in. Our team will respond within 24 hours with a customized proposal.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#1C4BB6] text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Need a Custom Solution?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Every facility has unique needs. Let's create a tailored solution for your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#1C4BB6] hover:bg-gray-100 px-12 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:scale-105">
              Schedule a Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
